/*
 * Sticky add-to-cart bar behaviour (main-product sticky_cart block).
 *
 * 1. <sticky-atc-plan>: One-time / Subscribe toggle. The Recharge widget owns the selling
 *    plan; this element selects its radios and copies the main form's selling_plan into the
 *    sticky form, which Recharge never touches.
 * 2. Hides the bar while the footer is on screen.
 */
if (!customElements.get('sticky-atc-plan')) {
  customElements.define(
    'sticky-atc-plan',
    class StickyAtcPlan extends HTMLElement {
      connectedCallback() {
        this.mainForm = document.getElementById(this.dataset.formId);
        this.altForm = document.getElementById(this.dataset.altFormId);
        if (!this.mainForm || !this.altForm) return;

        this.options = this.querySelectorAll('[data-plan]');
        this.options.forEach((option) =>
          option.addEventListener('click', () => this.selectPlan(option.dataset.plan))
        );

        // Recharge renders outside the form and injects selling_plan late, so watch the whole info column.
        this.infoContainer = this.mainForm.closest('.product__info-container') || document;
        this.sync = this.sync.bind(this);
        this.infoContainer.addEventListener('change', this.sync);
        this.infoContainer.addEventListener('input', this.sync);
        this.observer = new MutationObserver(this.sync);
        this.observer.observe(this.infoContainer, {
          subtree: true,
          attributes: true,
          childList: true,
          attributeFilter: ['class', 'value'],
        });
        this.sync();
        this.watchWidget();
      }

      // Only offer the toggle when the Recharge widget itself shows a Subscribe option: Shopify can
      // expose selling plans that Recharge chooses not to display (e.g. the oil-free serum).
      async watchWidget() {
        await customElements.whenDefined('recharge-subscription-widget');
        const widget = this.infoContainer.querySelector('recharge-subscription-widget');
        const root = widget?.shadowRoot;
        if (!root) return;

        const update = () => {
          this.hidden = !root.querySelector('input[name="purchaseOption"][value="subscription"]');
        };
        this.widgetObserver = new MutationObserver(update);
        this.widgetObserver.observe(root, { childList: true, subtree: true });
        update();
      }

      disconnectedCallback() {
        this.widgetObserver?.disconnect();
        this.infoContainer?.removeEventListener('change', this.sync);
        this.infoContainer?.removeEventListener('input', this.sync);
        this.observer?.disconnect();
      }

      // Recharge's subscription widget (v2) renders its purchase-option radios in a shadow root.
      selectPlan(plan) {
        const widget = this.infoContainer.querySelector('recharge-subscription-widget');
        const input = widget?.shadowRoot?.querySelector(`input[name="purchaseOption"][value="${plan}"]`);
        if (!input || input.checked) return;

        // Selecting the off-screen radio focuses it, which scrolls the page back up to the widget.
        const { scrollX, scrollY } = window;
        input.click();
        window.scrollTo(scrollX, scrollY);
        requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
      }

      sync() {
        const sellingPlan = new FormData(this.mainForm).get('selling_plan') || '';

        this.options.forEach((option) => {
          const active = (option.dataset.plan === 'subscription') === Boolean(sellingPlan);
          option.setAttribute('aria-pressed', String(active));
        });

        let altInput = this.altForm.querySelector('input[name="selling_plan"]');
        if (!altInput) {
          altInput = document.createElement('input');
          altInput.type = 'hidden';
          altInput.name = 'selling_plan';
          this.altForm.prepend(altInput);
        }
        altInput.value = sellingPlan;
        altInput.disabled = !sellingPlan;
      }
    }
  );
}

(() => {
  const footer = document.querySelector('footer.footer');
  const bars = document.querySelectorAll('animate-sticky.sticky-cart-wrapper');
  if (!footer || !bars.length || !('IntersectionObserver' in window)) return;

  new IntersectionObserver(([entry]) => {
    bars.forEach((bar) => bar.classList.toggle('sticky-cart-wrapper--at-footer', entry.isIntersecting));
  }).observe(footer);
})();
